import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Spinner, ListGroup, Badge } from 'react-bootstrap';
import { FaFileWord, FaFileAlt, FaTimes, FaHistory } from 'react-icons/fa';
import api from '../services/api';

const RestructuringDocumentsModal = ({ show, onHide, reportId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [restructuringHistory, setRestructuringHistory] = useState([]);

    useEffect(() => {
        if (show && reportId) {
            fetchRestructuringHistory();
        }
    }, [show, reportId]);

    const fetchRestructuringHistory = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await api.get(`/restructuring/history/${reportId}`);

            if (response.data.success) {
                setRestructuringHistory(response.data.history);
            }
        } catch (error) {
            console.error('Yapılandırma geçmişi hatası:', error);
            setError('Yapılandırma geçmişi yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadProtokol = async (restructuring) => {
        try {
            if (!restructuring.documents || restructuring.documents.length === 0) {
                alert('Protokol belgesi bulunamadı');
                return;
            }

            const protokol = restructuring.documents.find(doc => doc.type === 'protokol');
            if (!protokol) {
                alert('Protokol belgesi bulunamadı');
                return;
            }

            // Backend'den dosyayı indir
            const response = await api.get(
                `/restructuring/${restructuring._id}/documents/protokol/download`,
                { responseType: 'blob' }
            );

            // Dosyayı indir
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', protokol.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Protokol indirme hatası:', error);
            alert('Protokol indirilirken hata oluştu');
        }
    };

    const handleOpenSenet = (restructuringId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                alert('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
                return;
            }

            const senetUrl = `${process.env.REACT_APP_API_URL}/restructuring/${restructuringId}/senet?token=${token}`;
            window.open(senetUrl, '_blank', 'noopener,noreferrer');
        } catch (error) {
            console.error('Senet açılırken hata:', error);
            alert('Senet sayfası açılırken hata oluştu');
        }
    };

    const handleOpenSenetTevdi = (restructuringId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                alert('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
                return;
            }

            const senetTevdiUrl = `${process.env.REACT_APP_API_URL}/restructuring/${restructuringId}/senet-tevdi?token=${token}`;
            window.open(senetTevdiUrl, '_blank', 'noopener,noreferrer');
        } catch (error) {
            console.error('Senet Tevdi açılırken hata:', error);
            alert('Senet Tevdi sayfası açılırken hata oluştu');
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('tr-TR');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaHistory className="me-2 text-secondary" />
                    Yapılandırma Belgeleri
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Yükleniyor...</span>
                        </Spinner>
                        <p className="mt-3 text-muted">Yapılandırma geçmişi yükleniyor...</p>
                    </div>
                ) : restructuringHistory.length === 0 ? (
                    <Alert variant="info" className="text-center">
                        <p className="mb-0">Bu rapor için henüz yapılandırma bulunmuyor.</p>
                    </Alert>
                ) : (
                    <ListGroup variant="flush">
                        {restructuringHistory.map((restructuring, index) => (
                            <ListGroup.Item key={restructuring._id} className="px-0">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div>
                                        <h6 className="mb-1">
                                            <Badge bg="secondary" className="me-2">
                                                Yapılandırma-{index + 1}
                                            </Badge>
                                            {formatDate(restructuring.restructureDate)}
                                        </h6>
                                        <small className="text-muted">
                                            Kalan Borç: {formatCurrency(restructuring.originalDebt)} →
                                            Yeni Vade: {restructuring.newTerms.term} ay →
                                            Aylık: {formatCurrency(restructuring.newTerms.monthlyPayment)}
                                        </small>
                                    </div>
                                    <Badge
                                        bg={restructuring.status === 'active' ? 'success' : 'secondary'}
                                        className="ms-2"
                                    >
                                        {restructuring.status === 'active' ? 'Aktif' : 'Tamamlandı'}
                                    </Badge>
                                </div>

                                <div className="d-flex flex-column gap-2">
                                    {/* Protokol */}
                                    <div className="d-flex align-items-center">
                                        <FaFileWord className="text-primary me-2" size={20} />
                                        {restructuring.documents && restructuring.documents.length > 0 ? (
                                            <Button
                                                variant="link"
                                                className="p-0 text-start text-decoration-none"
                                                onClick={() => handleDownloadProtokol(restructuring)}
                                            >
                                                <strong>Yapılandırma Protokolü</strong>
                                                <small className="text-muted d-block">
                                                    {restructuring.documents[0].filename}
                                                </small>
                                            </Button>
                                        ) : (
                                            <span className="text-muted">
                                                <strong>Yapılandırma Protokolü</strong>
                                                <small className="d-block">Belge bulunamadı</small>
                                            </span>
                                        )}
                                    </div>

                                    {/* Senet */}
                                    <div className="d-flex align-items-center">
                                        <FaFileAlt className="text-warning me-2" size={20} />
                                        <Button
                                            variant="link"
                                            className="p-0 text-start text-decoration-none"
                                            onClick={() => handleOpenSenet(restructuring._id)}
                                        >
                                            <strong>Yeni Senet</strong>
                                            <small className="text-muted d-block">
                                                {restructuring.newTerms.term} adet taksit seneti
                                            </small>
                                        </Button>
                                    </div>

                                    {/* Senet Tevdi */}
                                    <div className="d-flex align-items-center">
                                        <FaFileAlt className="text-info me-2" size={20} />
                                        <Button
                                            variant="link"
                                            className="p-0 text-start text-decoration-none"
                                            onClick={() => handleOpenSenetTevdi(restructuring._id)}
                                        >
                                            <strong>Yeni Senet Tevdi</strong>
                                            <small className="text-muted d-block">
                                                Banka tevdi formu
                                            </small>
                                        </Button>
                                    </div>
                                </div>

                                {restructuring.notes && (
                                    <Alert variant="light" className="mt-3 mb-0">
                                        <small><strong>Not:</strong> {restructuring.notes}</small>
                                    </Alert>
                                )}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    <FaTimes className="me-2" />
                    Kapat
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RestructuringDocumentsModal;